import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Button, Col, Input, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import Select from 'react-select';
import _ from 'lodash';
import { ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, dateHelpers, UserContext, constants } from '../utils';

export default function ConsignedInventoryUsage(props) {
   const { currentUser } = useContext(UserContext);
   const [allSites, setAllSites] = useState([])
   const [caseNumber, setCaseNumber] = useState("");
   const [ccEmail, setCCEmail] = useState("");
   const [consignedCustomers, setConsignedCustomers] = useState([]);
   const [dataDict, setDataDict] = useState(null)
   const [initialized, setInitialized] = useState(false);
   const [loaded, setLoaded] = useState(true);
   const [saved, setSaved] = useState(false);
   const [selectedCustomer, setSelectedCustomer] = useState({ value: "" });
   const [selectedSite, setSelectedSite] = useState({ value: "" });
   const [validSites, setValidSites] = useState([])
   const [working, setWorking] = useState(false);

   useEffect(() => {
      document.title = `Consigned Inventory Use`;
      if (!initialized && currentUser) {
         setCCEmail(currentUser.email)
         if (currentUser.isConsignedCustomerRole) {
            setSelectedCustomer({ value: "FLEX05", label: "" })
            setSelectedSite({ value: "FLEX05", label: "" })
            setInitialized(true)
            setLoaded(false)
         } else {
            api
               .fetch(`Customer/LoadCISDropdowns`)
               .then(r => {
                  if (r.data.success) {
                     setConsignedCustomers(_.map(r.data.customers, cst => {
                        return { value: cst.customerId, label: cst.customerNumName }
                     }))
                     setAllSites(_.map(r.data.sites, site => {
                        return { value: site.siteId, label: `${site.siteId} | ${site.siteDescription}`, customer: site.customerId }
                     }))
                  }
               })
               .catch(api.catchHandler)
               .finally(() => setInitialized(true))
         }
      }

      if (initialized && !loaded && currentUser.isAnyCustomerTypeRole) {
         api
            .post(`Customer/GetConsignedInventoryCust`, {
               CustomerId: selectedCustomer.value,
               SiteId: selectedSite.value
            })
            .then(r => {
               if (r.data.success) {
                  setDataDict(r.data.inventoryDict)
               }
            })
            .catch(api.catchHandler)
            .finally(() => setLoaded(true))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialized, loaded, currentUser])

   useEffect(() => {
      if (initialized && !loaded && !currentUser.isAnyCustomerTypeRole) {
         api
            .post(`Customer/GetConsignedInventory`, {
               CustomerId: selectedCustomer.value,
               SiteId: selectedSite.value
            })
            .then(r => {
               if (r.data.success) {
                  setDataDict(r.data.inventoryDict)
               }
            })
            .catch(api.catchHandler)
            .finally(() => setLoaded(true))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialized, loaded, currentUser])

   function addCase() {
      if (caseNumber.match(/wo-\d{8}-\d+/i) === null) {
         toast.error("Invalid Case Number")
         return;
      }
      setWorking(true);
      api
         .fetch(`Customer/RepairConsignedCase`, { caseNum: caseNumber })
         .then(response => {
            if (response && response.data && response.data.success) {
               toast.success("Case Successfully added back");
               setLoaded(false);
               setCaseNumber("");
            } else {
               if (_.isString(response?.data?.message)) {
                  toast.error(response?.data?.message);
               }
               setCaseNumber("");
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorking(false);
         })
   }

   function createPayload(submitting) {
      const inventory = _.flatMap(dataDict);

      _.forEach(inventory, item => {
         if (!item.checkOnHandInventory) {
            item.checkedOnHand = !item.checkedUsed;
         }
      });

      return {
         inventories: inventory,
         customerId: selectedCustomer.value,
         customerName: selectedCustomer.label,
         siteId: selectedSite.value,
         ccEmail: ccEmail,
         showUsedAndOnHand: true,
         submitting: submitting
      };
   }

   function handleConsignedInventoryReport(showUsedAndOnHand) {
      const filename = `${showUsedAndOnHand ? "Audit_" : ""}Consigned_Inventory_${selectedCustomer.value}_${dateHelpers.toLongDateString(new Date())}.pdf`

      setWorking(true)
      api
         .postDownloadBinary("Report/ConsignedInventory", { CustomerId: selectedCustomer.value, SiteId: selectedSite.value, showUsedAndOnHand: showUsedAndOnHand }, constants.MIME_TYPES.pdf, filename)
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorking(false);
         });
   }

   function handleCustomerChange(customer) {
      setSelectedCustomer(customer)
      const goodSites = _.filter(allSites, site => { return site.customer === customer.value })
      setValidSites(goodSites)
      setSelectedSite(goodSites[0])
      setLoaded(false)
   }

   function handleCheck(itemGroup, index) {
      var dataDictCopy = _.assign({}, dataDict);
      if (dataDictCopy[itemGroup][index].checkOnHandInventory) {
         dataDictCopy[itemGroup][index].onHand = !dataDictCopy[itemGroup][index].onHand;
         dataDictCopy[itemGroup][index].checkedOnHand = !dataDictCopy[itemGroup][index].checkedOnHand;
      } else {
         dataDictCopy[itemGroup][index].caseUsed = !dataDictCopy[itemGroup][index].caseUsed;
         dataDictCopy[itemGroup][index].checkedUsed = !dataDictCopy[itemGroup][index].checkedUsed;
      }
      setDataDict(dataDictCopy);
   }

   function handleSave() {
      setWorking(true)

      api
         .post(`Customer/SaveConsignedInventoryUsage`, createPayload(false))
         .then(response => {
            if (response.data.success) {
               toast.success("Consigned Inventory Successfully Saved");
            } else {
               if (_.isString(response?.data?.message)) {
                  toast.error(response?.data?.message);
               }
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setSaved(true);
            setWorking(false);
         })
   }

   function handleSubmit() {
      setWorking(true)

      api
         .post(`Customer/SaveConsignedInventoryUsage`, createPayload(true))
         .then(response => {
            if (response.data.success) {
               toast.success("Consigned Inventory Successfully Submitted");
            } else {
               if (_.isString(response?.data?.message)) {
                  toast.error(response?.data?.message);
               }
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorking(false);
            setLoaded(false);
         })
   }

   return (
      <DesktopContainer screenName="Report Consigned Inventory Use">
         {!currentUser?.isAnyCustomerTypeRole &&
            (<div className="filter-header mb-3">
               <Row className="justify-content-center">
                  <Col>
                     <Row className="align-items-center mt-2">
                        <Col className="text-end text-white h5" xs={2}>
                           Customer:
                        </Col>
                        <Col xs={9}>
                           <Select
                              classNamePrefix='serviceThreadSelect'
                              options={consignedCustomers}
                              onChange={e => handleCustomerChange(e)}
                              disabled={working}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col className="text-end text-white h5" xs={2}>
                           Site:
                        </Col>
                        <Col xs={9}>
                           <Select
                              classNamePrefix='serviceThreadSelect'
                              options={validSites}
                              value={selectedSite}
                              onChange={e => setSelectedSite(e)}
                              disabled={working}
                           />
                        </Col>
                     </Row>
                  </Col>
               </Row>
            </div>)}
         <ServiceThreadSpinner on={!loaded}>
            {dataDict && _.keys(dataDict).length
               ? (<>
                  <Row className="mb-2">
                     <Col className="text-center">
                        <span
                           className="fw-bold"
                           children={`Item(s) in bold will be auto invoiced. (Older than 60 days)`}
                        />
                     </Col>
                  </Row>
                  <Table className="data-table w-75 m-auto">
                     <thead className="table-header">
                        <tr>
                           <th>Item Number:</th>
                           <th className="text-center">Case Number:</th>
                           <th className="text-center">Date Shipped:</th>
                           <th className="text-center">Quantity Shipped:</th>
                           <th className="text-center">Verified Used Check:</th>
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(dataDict, (items, itemGroup) => {
                           return _.map(items, (item, index) => {
                              return (
                                 <Fragment key={`${itemGroup}.${index}`}>
                                    {index === 0 && (
                                       <tr className="table-header">
                                          <th colSpan={5}>
                                             <Row>
                                                <Col>
                                                   <div className="m-0 p-0">{`${item.customerItemNumber} | ${item.customerItemDescription}`}</div>
                                                   <div className="m-0 p-0">{item.itemWithDescription}</div>
                                                   <div className={`${item.customerPONumber.length ? "" : "d-none"}`}>{`PO: ${item.customerPONumber}`}</div>
                                                </Col>
                                             </Row>
                                          </th>
                                       </tr>
                                    )}
                                    <tr className={`text-center tr-${index % 2 === 0 ? "primary" : "secondary"} ${item.dueForInvoice && 'fw-bold'}`}>
                                       <td />
                                       <td>{item.caseNumber}</td>
                                       <td>{dateHelpers.toLongDateString(item.dateReceived)}</td>
                                       <td>{item.qtyReceived}</td>
                                       <td>
                                          <Input
                                             type="checkbox"
                                             checked={item.checkOnHandInventory ? item.onHand : item.caseUsed}
                                             disabled={item.dueForInvoice || working}
                                             onChange={() => handleCheck(itemGroup, index)}
                                          />
                                       </td>
                                    </tr>
                                 </Fragment>
                              )
                           })
                        })}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           <td />
                           <td />
                           <td />
                           <td />
                           <td />
                        </tr>
                     </tfoot>
                  </Table>
                  <Row className="w-75 m-auto my-3">
                     <Col xs={{ offset: 8, size: 4 }}>
                        <Button
                           size="sm"
                           className="shredButton float-end"
                           onClick={handleSave}
                           disabled={working}
                        >
                           Save and show report options
                        </Button>
                     </Col>
                  </Row>
                  {saved && (
                     <>
                        <Row className="w-75 m-auto my-3">
                           <Col className="text-end fw-bold pt-2" xs={2}>
                              CC Email:
                           </Col>
                           <Col xs={9}>
                              <Input value={ccEmail} onChange={e => setCCEmail(e.target.value)} />
                           </Col>
                        </Row>
                        <Row className="w-75 m-auto my-3 justify-content-evenly">
                           <Col xs={3}>
                              <Button
                                 size="sm"
                                 className="shredButton w-100"
                                 disabled={working}
                                 onClick={() => handleConsignedInventoryReport(false)}
                              >
                                 Consigned Inventory<br /> Report
                              </Button>
                           </Col>
                           <Col xs={3}>
                              <Button
                                 size="sm"
                                 className="shredButton w-100"
                                 children="Audit Consigned Inventory Report"
                                 disabled={working}
                                 onClick={() => handleConsignedInventoryReport(true)}
                              >
                                 Audit Consigned <br /> Inventory Report
                              </Button>
                           </Col>
                           <Col xs={3}>
                              <Button
                                 size="sm"
                                 className="shredButton w-100"
                                 disabled={working}
                                 onClick={handleSubmit}
                              >
                                 Submit Consigned <br /> Reporting
                              </Button>
                           </Col>
                        </Row>
                     </>
                  )}
                  {!currentUser?.isAnyCustomerTypeRole &&
                     (<>
                        <hr />
                        <Row className="w-75 m-auto mb-2">
                           <Col>
                              <div className="text-center">
                                 Type in the case # below you would like to add back. This only adds cases back to this page no GP Data is changed
                              </div>
                           </Col>
                        </Row>
                        <Row className="w-75 m-auto">
                           <Col className="text-end fw-bold pt-2" xs={2}>
                              Case Number:
                           </Col>
                           <Col xs={7}>
                              <Input value={caseNumber} onChange={e => setCaseNumber(e.target.value)} />
                           </Col>
                           <Col xs={3}>
                              <Button
                                 className="shredButton"
                                 children="Add Case"
                                 onClick={addCase}
                                 disabled={working || !(caseNumber && caseNumber.length && caseNumber.length > 12)}
                              />
                           </Col>
                        </Row>
                     </>)}
               </>)
               : (<Row className="mt-3 mb-2">
                  <Col className="text-center">
                     <span className="h5">No Consigned Inventory Information Found</span>
                  </Col>
               </Row>)
            }
         </ServiceThreadSpinner>
      </DesktopContainer >
   );
}